import React, { useRef, useEffect, KeyboardEvent, MouseEvent, PropsWithChildren } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { transparentize } from 'polished';

import { elevationTokens, colorTokens } from '../../tokens/globals/tokens';

import getGlobalProps from '../../helpers/constants/global-props';
import Portal from '../_common/Portal';

export interface DialogProps {
  /** Marks the element with a given ID to make it easier to select in tests */
  'data-testid'?: string | number;
  /** Callback for the close event, either by clicking on backdrop or pressing Escape key */
  onRequestClose?: (event: KeyboardEvent | MouseEvent) => void;
  /** @ignore */
  container?: HTMLElement;
}

const DialogWrapper = styled.div`
  background-color: ${transparentize(0.2, colorTokens.GlobalBrandBlack)};
  bottom: 0;
  left: 0;
  outline: none;
  overflow-x: hidden;
  overflow-y: auto;
  position: fixed;
  right: 0;
  top: 0;
  z-index: ${elevationTokens.GlobalDialogDefault};
`;

const DialogGlobalStyle = createGlobalStyle`
  body {
    overflow: hidden;
  }
`;

const globalProps = getGlobalProps({});

/**
 * @purpose Show content over the page in a restricted lightbox context. The content is rendered on a Portal that is appended to the Body
 * @group Modal
 */
export function Dialog({ onRequestClose, container, ...props }: PropsWithChildren<DialogProps>) {
  const handleOnRequestClose = (event: React.KeyboardEvent | React.MouseEvent) => {
    if (event.target !== event.currentTarget) {
      return;
    }

    if ('key' in event && event.key !== 'Escape') {
      return;
    }

    if (onRequestClose) {
      onRequestClose(event);
    }
  };

  // eslint-disable-next-line ssr-friendly/no-dom-globals-in-react-fc
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!dialogRef.current) {
      return;
    }

    if (['INPUT', 'TEXTAREA'].includes(document?.activeElement?.tagName || '')) {
      /* Don't trap focus if the user is already focused on an input */
      return;
    }

    if (window?.self !== window?.top) {
      /* Don't trap focus if the dialog is in an iframe */
      return;
    }

    dialogRef.current.focus();
  }, []);

  return (
    <Portal container={container}>
      <>
        <DialogGlobalStyle />
        <DialogWrapper
          data-testid={props['data-testid']}
          onKeyDown={handleOnRequestClose}
          onMouseDown={handleOnRequestClose}
          role="dialog"
          ref={dialogRef}
          tabIndex={0}
          {...props}
          className={undefined}
          style={undefined}
          {...globalProps}
        >
          {props.children}
        </DialogWrapper>
      </>
    </Portal>
  );
}

Dialog.defaultProps = {
  children: undefined,
  container: undefined,
  'data-testid': undefined,
  onRequestClose: () => {},
};

export default Dialog;
